/**
 * Dependencies.
 */
import { Component, Mixins, Prop } from 'vue-property-decorator';

import OrderSurveyModel from '@/api/delivery-survey/delivery-survey.model';
import { deliverySurveyService as orderSurveyService } from '@/api/delivery-survey/delivery-survey.service';
import ERRORS from '@/modules/survey/constants/errors.constants';
import SurveyMixin from '@/modules/survey/mixins/survey-mixin';

/**
 * Component.
 */
@Component({
  name: 'survey-step-2-scene'
})
export default class extends Mixins(SurveyMixin) {
  /**
   * Property `survey`.
   */
  survey: OrderSurveyModel = null;

  /**
   * Property `hasCustomerDisliked`.
   */
  hasCustomerDisliked = false;

  /**
   * Property `hasCustomerLiked`.
   */
  hasCustomerLiked = false;

  /**
   * Property `selectedEntitySatisfaction`.
   */
  selectedEntitySatisfaction: string[] = [];

  /**
   * Property `points`.
   */
  points: number[] = [];

  /**
   * Property `pointsPercentages`.
   */
  pointsPercentages: number[] = [100, 50, 20, 10, 5];

  /**
   * Property `entityComment`.
   */
  entityComment: string = null;

  /**
   * Property `suggestedReward`.
   */
  suggestedReward: number = null;

  /**
   * Property `customReward`.
   */
  customReward: number = null;

  /**
   * Property `orderSurvey`;
   */
  @Prop({ default: null })
  orderSurvey: string;

  /**
   * Property `orderComment`;
   */
  @Prop({ default: null })
  orderComment: string;

  /**
   * Property `orderRating`;
   */
  @Prop({ default: null })
  orderRating: number;

  /**
   * Property `selectedOrderSatisfaction`;
   */
  @Prop({ default: null })
  selectedOrderSatisfaction: string;

  /**
   * Getter `driverName`.
   */
  get driverName() {
    const { driver = null } = this.survey || {};
    const { name = null } = driver || {};

    return name;
  }

  /**
   * Getter `userName`.
   */
  get userName() {
    const { user = null } = this.survey || {};
    const { name = null } = user || {};

    return name;
  }

  /**
   * Getter `availablePoints`.
   */
  get availablePoints() {
    const { availablePoints = null } = this.survey || {};

    return availablePoints;
  }

  /**
   * Getter `sentence`.
   */
  get sentence() {
    let sentence = this.i18n('SURVEY.SENTENCES.ENTITY', {
      data: { entity: this.userName }
    });

    if (this.isADriver) {
      sentence = this.i18n('SURVEY.SENTENCES.ENTITY', {
        data: { entity: this.i18n('SURVEY.LABELS.COURIER').toLowerCase() }
      }).replace('(a)', '');
    }

    return sentence;
  }

  /**
   * Getter `emptyImage`.
   */
  get emptyImage() {
    return 'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';
  }

  /**
   * Getter `isADriver`.
   */
  get isADriver() {
    const { user = null } = this.survey || {};

    return !user;
  }

  /**
   * Getter `driverPhoto`.
   */
  get driverPhoto() {
    const { isADriver } = this || {};
    const { driver = null } = this.survey || {};
    const { photoLink = null } = driver || {};

    return isADriver && photoLink ? photoLink : null;
  }

  /**
   * Getter `vehiclePhoto`.
   */
  get vehiclePhoto() {
    const { vehicle = null } = this.survey || {};
    const { photoLink = null } = vehicle || {};

    return photoLink;
  }

  /**
   * Getter `hasValidData`.
   */
  get hasValidData() {
    const hasComment = this.canShowEntityComment;

    return (
      ((this.hasCustomerDisliked &&
        this.selectedEntitySatisfaction.length > 0) ||
        (this.hasCustomerDisliked && this.dislike?.length === 0) ||
        this.hasCustomerLiked) &&
      (!hasComment ||
        (hasComment &&
          this?.entityComment?.length > 0 &&
          this?.entityComment?.length <= 120)) &&
      (!this.customReward ||
        (this.customReward >= 1 && this.customReward <= this.availablePoints))
    );
  }

  /**
   * Getter `canShowEntityComment`.
   */
  get canShowEntityComment() {
    return this.selectedEntitySatisfaction.includes('OTHER');
  }

  /**
   * Getter `orderSatisfaction`.
   */
  get orderSatisfaction() {
    const satisfaction = this.selectedOrderSatisfaction || '';
    const orderSatisfaction = satisfaction.length
      ? satisfaction.split(',')
      : [];

    return orderSatisfaction.sort().map(id => {
      const currentId = id;
      const rating = Number(this.orderRating);

      let reasons = [];
      switch (this.handleGetClientRatingLevel({ rating })) {
        case 'amazed':
          reasons = [...this.amazed];
          break;

        case 'satisfied':
          reasons = [...this.satisfied];
          break;

        default:
          reasons = [...this.unhappy];
          break;
      }

      const reason = reasons.find(({ id: itemId }) => currentId === itemId);
      const { label = null } = reason || {};

      return { id: currentId, label };
    });
  }

  /**
   * Getter `entitySatisfaction`.
   */
  get entitySatisfaction() {
    const entitySatisfaction = [...this.selectedEntitySatisfaction];

    return entitySatisfaction.sort().map(id => {
      const reasons = this.hasCustomerDisliked
        ? [...this.dislike]
        : [...this.like];

      const reason = reasons.find(({ id: itemId }) => id === itemId);
      const { label = null } = reason || {};

      return { id, label };
    });
  }

  /**
   * Getter `alreadySubmited`.
   */
  get alreadySubmited() {
    const { alreadySubmited } = this.survey || {};

    return alreadySubmited;
  }

  /**
   * Getter `orderCode`.
   */
  get orderCode() {
    const { delivery } = this.survey || {};
    const { orderCode = null } = delivery || {};

    return orderCode;
  }

  /**
   * Getter `clientName`.
   */
  get clientName() {
    const { delivery = null } = this.survey || {};
    const { client = null } = delivery || {};
    const { name = null } = client || {};

    return name;
  }

  /**
   * Lifecycle created hook.
   */
  async created() {
    const { commentsTypes = null, entityCommentsTypes = null, key } =
      this.$route?.params || {};
    const { orderRating: rating } = this || {};

    if (!rating) {
      this.$router.push({
        name: 'SurveyStep1',
        params: { key }
      });
    } else {
      this.survey = JSON.parse(this.orderSurvey);
      this.handleInitializeSuggestedPoints();
    }

    if (commentsTypes) {
      this.commentsTypes = JSON.parse(commentsTypes);
    }

    if (entityCommentsTypes) {
      this.entityCommentsTypes = JSON.parse(entityCommentsTypes);
    }
  }

  /**
   * Handle is numeric.
   */
  handleIsNumber(event) {
    const keysAllowed: string[] = this.numericKeysAllowed;
    const keyPressed: string = event.key;

    if (!keysAllowed.includes(keyPressed)) {
      event.preventDefault();
    }
  }

  /**
   * Handle initialize suggested points.
   */
  handleInitializeSuggestedPoints() {
    const { availablePoints = 0, pointsPercentages = [] } = this || {};

    if (availablePoints > 0) {
      let roundingUnits = 1;

      if (availablePoints >= 10 && availablePoints < 100) {
        roundingUnits = 10;
      }

      if (availablePoints >= 100) {
        roundingUnits = 100;
      }

      const fixedMaxPoints =
        availablePoints <= 1000
          ? Math.floor(availablePoints / roundingUnits) * roundingUnits
          : 1000;

      pointsPercentages.forEach(percentage => {
        const points = Math.floor((fixedMaxPoints * percentage) / 100);

        if (points >= 1) {
          this.points = [...this.points, points];
        }
      });
    }
  }

  /**
   * Handle dislike.
   */
  handleDislike() {
    this.hasCustomerLiked = false;
    this.hasCustomerDisliked = true;

    this.selectedEntitySatisfaction = [];

    this.entityComment = null;
    this.customReward = null;
    this.suggestedReward = null;
  }

  /**
   * Handle like.
   */
  handleLike() {
    this.hasCustomerDisliked = false;
    this.hasCustomerLiked = true;

    this.selectedEntitySatisfaction = [];

    this.entityComment = null;
  }

  /**
   * Handle select entity satisfaction.
   */
  handleSelectEntitySatisfaction({ id }: { id: string }) {
    const isAlreadySelected = this.selectedEntitySatisfaction.includes(id);

    if (id === 'OTHER' && isAlreadySelected) {
      this.entityComment = null;
    }

    this.selectedEntitySatisfaction = !isAlreadySelected
      ? [...this.selectedEntitySatisfaction, id]
      : this.selectedEntitySatisfaction.filter(itemId => itemId !== id);
  }

  /**
   * Handle suggested reward.
   */
  handleSuggestedReward({ points }: { points: number }) {
    const { suggestedReward } = this || {};

    if (points === suggestedReward) {
      this.suggestedReward = null;
    } else {
      this.suggestedReward = points;
    }

    this.customReward = null;
  }

  /**
   * Handle finish survey.
   */
  async handleFinishSurvey() {
    const { clientName } = this || {};
    const { key } = this.$route?.params || {};
    const {
      customReward = null,
      orderRating = null,
      suggestedReward = null
    } = this;
    const { user = null } = this.survey || {};
    const assignedPoints = suggestedReward || customReward;

    // Order Satisfation.
    const orderSatisfaction = [];
    this.orderSatisfaction.forEach(({ id: type }) => {
      let data: {
        other?: string;
        type: string;
      } = { type };

      if (type === 'OTHER') {
        data = { ...data, other: this.orderComment };
      }

      orderSatisfaction.push(data);
    });

    // Entity satisfation.
    const entitySatisfaction = [];
    this.entitySatisfaction.forEach(({ id: type }) => {
      let data: {
        other?: string;
        type: string;
      } = { type };

      if (type === 'OTHER') {
        data = { ...data, other: this.entityComment };
      }

      entitySatisfaction.push(data);
    });

    const surveyData = {
      ...(!!user
        ? // Service point.
        { user: { id: user?.id } }
        : // Driver.
        { driver: { id: this.survey?.driver?.id } }),
      assignedPoints: !!assignedPoints ? Number(assignedPoints) : 0,
      availablePoints: !!this.availablePoints
        ? Number(this.availablePoints)
        : 0,
      classification: Number(orderRating),
      comments: orderSatisfaction,
      delivery: { orderCode: this.orderCode },
      entityComments: entitySatisfaction,
      entityIsSatisfied: this.hasCustomerLiked
    };

    try {
      const endpoint = `${orderSurveyService.getModelEndpoint}/${key}`;

      await orderSurveyService.POST(endpoint, surveyData);

      this.$router.push({
        name: 'SurveyDone',
        params: { clientName, key }
      });
    } catch (error) {
      const { code: errorCode } = error?.raw || {};
      const hasPointsError = errorCode === ERRORS.INVALID_PROZIS_POINTS;

      if (hasPointsError) {
        this.survey = await this.handleGetOrderSurvey();
        this.handleInitializeSuggestedPoints();
      }

      const message = hasPointsError
        ? this.i18n('SURVEY.ERRORS.INVALID_PROZIS_POINTS')
        : this.i18n('MAIN.ERRORS.BASE');

      this.$kit.notify.error(this.i18n('SURVEY.LABELS.ERROR'), message);

      if (process.env.BUILD_MODE !== 'prd') {
        console.error({ error, method: 'handleFinishSurvey' });
      }
    }
  }
}
